import { useCallback, useEffect, useRef, useState } from "react";

/** What to do after each successful `fetchPoll` tick. */
export type AsyncPollDecision =
    | { kind: "continue" }
    | { kind: "complete"; run?: () => void | Promise<void> }
    | { kind: "fail"; message: string };

export type UseAsyncPollingOptions<T> = {
    /** One async fetch for the current poll key (e.g. job id). */
    fetchPoll: (key: string) => Promise<T>;
    /** Maps the fetch result to continue / complete / fail. */
    decide: (result: T) => AsyncPollDecision;
    pollIntervalMs?: number;
    maxAttempts?: number;
    timeoutMessage?: string;
};

export type UseAsyncPollingResult = {
    startPolling: (key: string) => void;
    isPolling: boolean;
    error: string | null;
};

const DEFAULT_POLL_INTERVAL_MS = 1000;
const DEFAULT_MAX_ATTEMPTS = 100;

/**
 * Generic interval polling: calls `fetchPoll`, then `decide`, until complete, fail, timeout, or unmount.
 */
export function useAsyncPolling<T>(options: UseAsyncPollingOptions<T>): UseAsyncPollingResult {
    const {
        fetchPoll,
        decide,
        pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
        maxAttempts = DEFAULT_MAX_ATTEMPTS,
        timeoutMessage = "Polling timed out",
    } = options;

    const fetchPollRef = useRef(fetchPoll);
    fetchPollRef.current = fetchPoll;
    const decideRef = useRef(decide);
    decideRef.current = decide;

    const [isPolling, setIsPolling] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const cancelledRef = useRef(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const attemptRef = useRef(0);

    const clearPollTimer = useCallback(() => {
        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    useEffect(() => {
        cancelledRef.current = false;
        return () => {
            cancelledRef.current = true;
            clearPollTimer();
        };
    }, [clearPollTimer]);

    const runPollLoop = useCallback(
        (key: string) => {
            attemptRef.current = 0;

            const scheduleNext = (poll: () => void) => {
                timeoutRef.current = setTimeout(poll, pollIntervalMs);
            };

            const poll = async () => {
                if (cancelledRef.current) {
                    return;
                }

                attemptRef.current += 1;
                if (attemptRef.current > maxAttempts) {
                    setIsPolling(false);
                    setError(timeoutMessage);
                    return;
                }

                try {
                    const result = await fetchPollRef.current(key);
                    if (cancelledRef.current) {
                        return;
                    }

                    const decision = decideRef.current(result);

                    if (decision.kind === "complete") {
                        setIsPolling(false);
                        if (decision.run) {
                            await decision.run();
                        }
                        return;
                    }
                    if (decision.kind === "fail") {
                        setIsPolling(false);
                        setError(decision.message);
                        return;
                    }

                    scheduleNext(poll);
                } catch (e) {
                    if (!cancelledRef.current) {
                        setIsPolling(false);
                        setError(e instanceof Error ? e.message : String(e));
                    }
                }
            };

            void poll();
        },
        [maxAttempts, pollIntervalMs, timeoutMessage]
    );

    const startPolling = useCallback(
        (key: string) => {
            setError(null);
            setIsPolling(true);
            clearPollTimer();
            runPollLoop(key);
        },
        [clearPollTimer, runPollLoop]
    );

    return { startPolling, isPolling, error };
}
